import { useApolloClient, useMutation } from "@apollo/client";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";

import {
  PROCESS_IMAGE,
  UPDATE_IMAGE_ORDER,
  UPLOAD_IMAGE,
} from "../../../../../graphql/mutation/Image";
import useUploadProgress from "../../../../../hooks/useUploadProgress";
import { Button, Modal } from "../../../../ui";
import Toggle from "../../../../ui/Input/components/Toggle";
import { reorder, sortFilesByTrailingNumber } from "../utils";

export const uploadFileWithProgress = ({ file, onProgress, signal, url }) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const abortRequest = () => request.abort();
    const removeAbortListener = () =>
      signal?.removeEventListener("abort", abortRequest);
    const finish = callback => {
      removeAbortListener();
      callback();
    };

    request.open("PUT", url);
    request.setRequestHeader("Content-Type", "image/tif");
    request.upload.onprogress = event => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(100);
        finish(resolve);
      } else {
        finish(() => reject(new Error(`Upload failed (${request.status})`)));
      }
    };
    request.onerror = () =>
      finish(() => reject(new Error("Upload failed due to a network error")));
    request.onabort = () =>
      finish(() => reject(new Error("Upload was cancelled")));

    if (signal?.aborted) {
      reject(new Error("Upload was cancelled"));
      return;
    }

    signal?.addEventListener("abort", abortRequest, { once: true });
    request.send(file);
  });

const getUploadStatusLabel = ({ progress = 0, status = "ready" } = {}) => {
  const labels = {
    complete: "Complete",
    failed: "Failed",
    processing: "Processing",
    queued: "Waiting",
    ready: "Ready",
  };

  return status === "uploading" ? `${progress}%` : labels[status];
};

/**
 * Byte-weighted progress across the files in the dialog. Each file reports its
 * own percent while its PUT is in flight; the summary bar is that, weighted by
 * size, rather than a jump when the whole file has finished.
 */
export const uploadProgressPercent = (images = [], uploadStates = {}) => {
  const totalBytes = images.reduce(
    (sum, image) => sum + (image.file?.size ?? 0),
    0,
  );
  if (totalBytes <= 0) return 0;

  const uploadedBytes = images.reduce((sum, image) => {
    const fraction = (uploadStates[image.url]?.progress ?? 0) / 100;
    return sum + fraction * (image.file?.size ?? 0);
  }, 0);

  return (uploadedBytes / totalBytes) * 100;
};

export const ensurePrimaryImage = (images, { defaultToFirst = true } = {}) => {
  const primaryImage =
    images.find(image => image.isPrimary) ??
    (defaultToFirst ? images[0] : undefined);

  return images.map(image => ({
    ...image,
    isPrimary: image.url === primaryImage?.url,
  }));
};

export const buildImageOrder = ({
  artworkId,
  currentImages,
  uploadedImages,
}) => {
  const orderedCurrentImages = [...currentImages].sort(
    (first, second) =>
      (first.order ?? Number.MAX_SAFE_INTEGER) -
      (second.order ?? Number.MAX_SAFE_INTEGER),
  );
  const primaryImage = uploadedImages.find(image => image.isPrimary);
  const orderedUploadedImages = primaryImage
    ? [
        primaryImage,
        ...uploadedImages.filter(
          image => image.archiveId !== primaryImage.archiveId,
        ),
      ]
    : uploadedImages;
  const orderedImages = primaryImage
    ? [
        primaryImage,
        ...orderedCurrentImages,
        ...orderedUploadedImages.slice(1),
      ]
    : [...orderedCurrentImages, ...orderedUploadedImages];

  return orderedImages.map((image, index) => ({
    artworkId,
    archiveId: image.archiveId,
    order: index + 1,
  }));
};

const ImageUploadRow = props => {
  const {
    disabled,
    image,
    isPrimary,
    onPrimaryChange,
    onRemove,
    uploadState = {},
  } = props;
  const { progress = 0, status = "ready" } = uploadState;
  const isComplete = status === "complete";
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.url, disabled });

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="border-t border-gray-200 bg-white"
    >
      <td className="max-w-0 truncate px-3 py-2" title={image.file.name}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={`Reorder ${image.file.name}`}
            disabled={disabled}
            className="cursor-grab text-gray-400 hover:text-black disabled:cursor-default"
            {...attributes}
            {...listeners}
          >
            <Bars3Icon className="h-4 w-4" />
          </button>
          <span className="truncate">{image.file.name}</span>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-gray-600">
        {(image.file.size / (1024 * 1024)).toFixed(2)} MB
      </td>
      <td className="px-3 py-2">
        <label className="sr-only" htmlFor={`primary-${image.url}`}>
          Make {image.file.name} primary
        </label>
        <Toggle
          name={`primary-${image.url}`}
          value={isPrimary}
          disabled={disabled || isComplete}
          onChange={onPrimaryChange}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex min-w-40 items-center gap-2">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
            <div
              className={`h-full rounded-full transition-all ${
                status === "failed" ? "bg-red-600" : "bg-black"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="w-16 text-xs text-gray-600">
            {getUploadStatusLabel(uploadState)}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 text-right align-middle">
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled || isComplete}
          aria-label={`Remove ${image.file.name}`}
          className="inline-flex h-8 w-8 cursor-pointer items-center justify-center text-gray-500 hover:text-black"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </td>
    </tr>
  );
};

const UploadImage = props => {
  const { artwork, currentImages = [], mainCloseModal } = props;
  const client = useApolloClient();
  const hasCurrentImages = currentImages.length > 0;
  // Refetch when the files are actually in S3. Doing it here, as the signed
  // URLs come back, re-renders the artwork page mid-PUT.
  const [uploadImage, { loading, reset: resetUploadMutation }] =
    useMutation(UPLOAD_IMAGE);
  const [processImage, { reset: resetProcessMutation }] =
    useMutation(PROCESS_IMAGE);
  const [
    updateImageOrder,
    { loading: isUpdatingImageOrder, reset: resetImageOrderMutation },
  ] = useMutation(UPDATE_IMAGE_ORDER({ imgT: true, order: true }), {
    refetchQueries: ["FetchArtwork", "FetchInstance"],
  });
  const [uploadStates, setUploadStates] = useState({});
  const [hasUploadStarted, setHasUploadStarted] = useState(false);
  const [hasUploadedBatch, setHasUploadedBatch] = useState(false);
  const [preparedImages, setPreparedImages] = useState([]);
  const [imageOrderError, setImageOrderError] = useState(false);
  const fileInputRef = useRef();

  const {
    isUploading,
    failed: failedUploads,
    start: startUpload,
    reset: resetUpload,
  } = useUploadProgress();

  const formik = useFormik({
    initialValues: {
      images: [],
    },
    enableReinitialize: true,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const images = formik?.values?.images ?? [];
  const displayedProgress = uploadProgressPercent(images, uploadStates);
  const pendingImages = images.filter(
    image => uploadStates[image.url]?.status !== "complete",
  );
  const imagesToUpload = pendingImages;
  const primaryImage = imagesToUpload.find(image => image.isPrimary);
  const isBusy = loading || isUploading || isUpdatingImageOrder;
  const hasIncompleteUpload = isBusy;
  const uploadSuccess = hasUploadedBatch && !hasIncompleteUpload;

  const updateUploadState = (imageUrl, state) => {
    setUploadStates(previous => ({
      ...previous,
      [imageUrl]: { ...previous[imageUrl], ...state },
    }));
  };

  const saveUploadedImageOrder = async uploadedImages => {
    setImageOrderError(false);
    const response = await updateImageOrder({
      variables: {
        input: {
          newImageOrder: buildImageOrder({
            artworkId: artwork?.id,
            currentImages,
            uploadedImages,
          }),
        },
      },
    });

    if (!response?.data?.updateImageOrder?.success) {
      throw new Error("Image order could not be saved");
    }
  };

  const completeUpload = async (uploadedImages, results = []) => {
    if (results.some(result => result.status === "rejected")) {
      return;
    }

    try {
      await saveUploadedImageOrder(uploadedImages);
      setHasUploadedBatch(true);
    } catch (error) {
      setImageOrderError(true);
      console.error("Failed to update the image order", error);
    }

    await client.refetchQueries({
      include: ["FetchArtwork", "FetchInstance"],
    });
  };

  const handleClick = async () => {
    setHasUploadStarted(true);
    setHasUploadedBatch(false);
    const orderedSelectedImages = [
      primaryImage,
      ...imagesToUpload.filter(image => image.url !== primaryImage?.url),
    ].filter(Boolean);
    setUploadStates(previous =>
      orderedSelectedImages.reduce(
        (states, image) => ({
          ...states,
          [image.url]: { progress: 0, status: "queued" },
        }),
        previous,
      ),
    );

    try {
      const response = await uploadImage({
        variables: {
          input: {
            artworkId: artwork?.id,
            imageCount: orderedSelectedImages.length,
          },
        },
      });

      const success = response?.data?.uploadImage?.success;
      const urls = response?.data?.uploadImage?.urls ?? [];

      if (!success || urls.length !== orderedSelectedImages.length) {
        throw new Error("Could not prepare all image uploads");
      }

      const preparedByImageId = new Map(
        urls.map((url, index) => {
          const image = orderedSelectedImages[index];
          return [
            image.url,
            {
              archiveId: url.archiveId,
              clientId: image.url,
              isPrimary: image.isPrimary,
            },
          ];
        }),
      );
      const nextPreparedImages = imagesToUpload.map(image =>
        preparedByImageId.get(image.url),
      );
      setPreparedImages(nextPreparedImages);

      const uploads = urls.map((url, index) => {
        const image = orderedSelectedImages[index];

        return {
          file: image.file,
          upload: async (file, { signal }) => {
            try {
              updateUploadState(image.url, {
                progress: 0,
                status: "uploading",
              });
              await uploadFileWithProgress({
                file,
                url: url.url,
                signal,
                onProgress: fileProgress =>
                  setUploadStates(previous => {
                    const existing = previous[image.url] ?? {};
                    if (
                      existing.status === "complete" ||
                      existing.status === "processing"
                    ) {
                      return previous;
                    }
                    return {
                      ...previous,
                      [image.url]: {
                        ...existing,
                        progress: Math.max(existing.progress ?? 0, fileProgress),
                        status: "uploading",
                      },
                    };
                  }),
              });
              updateUploadState(image.url, { status: "processing" });
              const processResponse = await processImage({
                variables: {
                  input: {
                    archiveId: url.archiveId,
                    path: url.path,
                    artworkId: artwork?.id,
                  },
                },
              });
              if (!processResponse?.data?.processImage?.success) {
                throw new Error("Image processing could not be started");
              }
              updateUploadState(image.url, {
                progress: 100,
                status: "complete",
              });
            } catch (error) {
              updateUploadState(image.url, { status: "failed" });
              throw error;
            }
          },
        };
      });

      const results = await startUpload(
        uploads.map(({ upload }) => upload),
        uploads.map(({ file }) => file),
      );
      await completeUpload(nextPreparedImages, results);
    } catch (error) {
      setUploadStates(previous =>
        orderedSelectedImages.reduce(
          (states, image) => ({
            ...states,
            [image.url]:
              states[image.url]?.status === "complete"
                ? states[image.url]
                : {
                    ...states[image.url],
                    status: "failed",
                  },
          }),
          previous,
        ),
      );
      console.error("Failed to upload images", error);
    }
  };

  const handleRetry = async () => {
    const results = await startUpload(
      failedUploads.map(({ fn }) => fn),
      failedUploads.map(({ file }) => file),
    );
    await completeUpload(preparedImages, results);
  };

  const handleRetryImageOrder = async () => {
    await completeUpload(preparedImages);
  };

  const resetModal = () => {
    formik?.resetForm();
    resetUpload();
    resetUploadMutation();
    resetProcessMutation();
    resetImageOrderMutation();
    setUploadStates({});
    setHasUploadStarted(false);
    setHasUploadedBatch(false);
    setPreparedImages([]);
    setImageOrderError(false);
  };

  useEffect(() => {
    if (!hasIncompleteUpload) {
      return undefined;
    }

    const warnBeforeUnload = event => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [hasIncompleteUpload]);

  const closeModalWithWarning = closeModal => {
    if (
      hasIncompleteUpload &&
      !window.confirm(
        "Images have not finished uploading. Close this dialog anyway?",
      )
    ) {
      return;
    }

    closeModal();
  };

  const handleAddImages = event => {
    const nextImages = sortFilesByTrailingNumber(
      Array.from(event.target.files),
    ).map((file, index) => ({
      url:
        window.crypto?.randomUUID?.() ??
        `${Date.now()}-${file.lastModified}-${images.length + index}`,
      file,
      isPrimary: false,
    }));

    formik.setFieldValue(
      "images",
      ensurePrimaryImage([...images, ...nextImages], {
        defaultToFirst: !hasCurrentImages,
      }),
    );
    event.target.value = "";
  };

  const uploadModalProps = {
    scale: "md",
    closeOnBackdrop: false,
    hideCloseButton: true,
    artwork,
    onOpen: resetModal,
    // renderBody keeps this tree in place. `body` is mounted as a new component
    // type on every render, which restarted the bars from zero on each tick.
    renderBody: ({ closeModal }) => {
      return (
        <div className="flex w-full min-w-0 flex-col p-3 sm:p-4 lg:p-8">
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="min-w-0 flex-1 text-xl font-bold sm:text-2xl">
              Upload Image for &quot;{artwork?.title}&quot;
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {!uploadSuccess && (
                <Button
                  label="Reset"
                  action="default"
                  disabled={isBusy}
                  onClick={resetModal}
                />
              )}
              <Button
                action={uploadSuccess ? "primary" : "default"}
                className={`${uploadSuccess ? "bg-green-600 hover:bg-green-700" : ""}`}
                label={uploadSuccess ? "Done" : "Cancel"}
                onClick={
                  uploadSuccess
                    ? mainCloseModal
                    : () => closeModalWithWarning(closeModal)
                }
              />
              {!uploadSuccess &&
                failedUploads.length === 0 &&
                !imageOrderError && (
                  <Button
                    label={isBusy ? "Uploading" : "Upload"}
                    disabled={imagesToUpload.length === 0 || isBusy}
                    onClick={handleClick}
                  />
                )}
            </div>
          </div>
          {hasCurrentImages && primaryImage && !uploadSuccess && (
            <div className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              Clicking Upload will change the primary image of this Artwork.
            </div>
          )}
          {isUploading && (
            <div className="mt-6 w-full">
              <div className="mb-1 flex justify-between">
                <span className="text-lg font-bold tracking-wide text-gray-600">
                  {Math.round(displayedProgress)}%
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-black transition-all duration-500"
                  style={{ width: `${displayedProgress}%` }}
                />
              </div>
            </div>
          )}
          {imageOrderError && (
            <div className="mt-6 rounded-md bg-red-50 p-4 text-red-800">
              <p className="font-semibold">
                Images uploaded, but the primary image could not be updated.
              </p>
              <Button
                className="mt-2"
                label="Retry Primary Update"
                disabled={isBusy}
                onClick={handleRetryImageOrder}
              />
            </div>
          )}
          {failedUploads.length > 0 && (
            <div className="mt-6 rounded-md bg-red-50 p-4 text-red-800">
              <p className="font-semibold">
                {failedUploads.length} uploads failed
              </p>
              <ul>
                {failedUploads.map(({ file = {} }) => {
                  const { name, size } = file;
                  return <li key={`${name}-${size}`}>{name}</li>;
                })}
              </ul>
              <Button
                className="mt-2"
                label="Retry Failed"
                onClick={handleRetry}
              />
            </div>
          )}
          <Transition
            show={!uploadSuccess}
            appear
            enter="transition-opacity duration-300 ease-out"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-300 ease-in"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="grid w-full grid-cols-1 pb-10 pt-5">
              <div className="flex">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/tiff, image/tif, .tiff, .tif"
                  multiple
                  onChange={handleAddImages}
                />
                <Button
                  action="default"
                  label="Add TIFF Images"
                  disabled={isBusy || hasUploadStarted}
                  onClick={() => fileInputRef.current?.click()}
                />
              </div>
              {images.length > 0 && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={({ active, over }) => {
                    if (!over || active.id === over.id) {
                      return;
                    }
                    const fromIndex = images.findIndex(
                      image => image.url === active.id,
                    );
                    const toIndex = images.findIndex(
                      image => image.url === over.id,
                    );
                    formik.setFieldValue(
                      "images",
                      reorder(images, fromIndex, toIndex),
                    );
                  }}
                >
                  <SortableContext
                    items={images.map(image => image.url)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="mt-4 overflow-x-auto rounded border border-gray-200">
                      <table className="w-full min-w-[44rem] table-fixed text-sm">
                        <thead className="bg-gray-50 text-left">
                          <tr>
                            <th className="px-3 py-2">Name</th>
                            <th className="w-28 px-3 py-2">Size</th>
                            <th className="w-24 px-3 py-2">Primary</th>
                            <th className="w-64 px-3 py-2">Progress</th>
                            <th className="w-10 px-3 py-2" />
                          </tr>
                        </thead>
                        <tbody>
                          {images.map((image, index) => (
                            <ImageUploadRow
                              key={image.url}
                              image={image}
                              uploadState={uploadStates[image.url]}
                              isPrimary={image.isPrimary}
                              disabled={isBusy || hasUploadStarted}
                              onPrimaryChange={isPrimary =>
                                formik.setFieldValue(
                                  "images",
                                  ensurePrimaryImage(
                                    images.map(currentImage => ({
                                      ...currentImage,
                                      isPrimary:
                                        isPrimary &&
                                        currentImage.url === image.url,
                                    })),
                                    {
                                      defaultToFirst: !hasCurrentImages,
                                    },
                                  ),
                                )
                              }
                              onRemove={() =>
                                formik.setFieldValue(
                                  "images",
                                  ensurePrimaryImage(
                                    images.filter(
                                      (_, imageIndex) => imageIndex !== index,
                                    ),
                                    {
                                      defaultToFirst: !hasCurrentImages,
                                    },
                                  ),
                                )
                              }
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </Transition>
        </div>
      );
    },
  };

  return (
    <Modal {...uploadModalProps}>
      <Button label="Upload Image" action="black" />
    </Modal>
  );
};

export default UploadImage;
